<template>
    <div class="relative my-0 mx-auto">
        <div
            class="relative h-[3px] bg-gray-300 rounded my-2.5 overflow-hidden
            before:absolute before:content-[''] before:h-[3px] before:bg-transparent before:block before:border-white before:border-solid
            before:border-x-5 before:left-[calc(20%-2.5px)] before:z-10 before:w-[calc(20%+5px)]
            after:absolute after:content-[''] after:h-[3px] after:bg-transparent after:block after:border-white after:border-solid
             after:border-x-5 after:right-[calc(20%-2.5px)] after:z-10 after:w-[calc(20%+5px)]">
            <div
                class="absolute h-[3px] rounded transition-width duration-500 ease-in-out transition-colors"
                :class="strengthOptions[passwordStrength]?.background"
                :data-score="passwordStrength" />
        </div>
        <div
            v-if="showHints && password"
            class="text-xs flex justify-end">
            <BasePopover
                size="sm"
                trigger="hover">
                <template #trigger>
                    <div
                        class="cursor-pointer flex gap-1 items-center"
                        :class="strengthOptions[passwordStrength]?.color">
                        <span>{{ strengthOptions[passwordStrength]?.label }}</span>
                        <Icon
                            size="5"
                            :name="strengthOptions[passwordStrength]?.icon" />
                    </div>
                </template>
                <div class="w-48 text-xs grid gap-2 text-xs">
                    <div>
                        <h5>
                            {{ $t('password.evaluation') }}
                        </h5>
                        <div
                            class="cursor-pointer flex gap-1 items-center"
                            :class="strengthOptions[passwordStrength]?.color">
                            <span>{{ strengthOptions[passwordStrength]?.label }}</span>
                            <Icon :name="strengthOptions[passwordStrength]?.icon" />
                        </div>
                    </div>
                    <template
                        v-if="passwordFeedback && passwordFeedback.warning || (passwordFeedback && passwordFeedback.suggestions.length)">
                        <template v-if="passwordFeedback.warning">
                            <h5>
                                {{ $t('password.feedback') }}
                            </h5>
                            <div
                                class="mb-2 font-medium">
                                {{ passwordFeedback.warning }}
                            </div>
                        </template>
                        <template
                            v-if="passwordFeedback.suggestions.length || notLongEnough">
                            <h5>
                                {{ $t('password.suggestions') }}
                            </h5>
                            <ul
                                class="list-disc list-inside">
                                <li
                                    v-for="(suggestion, index) in passwordFeedback.suggestions"
                                    :key="index">
                                    {{ suggestion }}
                                </li>
                                <li
                                    v-if="notLongEnough">
                                    {{ $t('password.suggestionMinLength', {length: secureLength}) }}
                                </li>
                            </ul>
                        </template>
                    </template>
                    <div>
                        <h5>
                            {{ $t('password.estimatedCrackTime') }}
                        </h5>
                        <div>
                            {{ passwordCrackTime?.onlineNoThrottling10PerSecond }}
                        </div>
                    </div>
                </div>
            </BasePopover>
        </div>
    </div>
</template>

<script setup>
import {zxcvbn} from '@zxcvbn-ts/core'

const props = defineProps({
    /**
     * Password min length.
     * Right now only visual for the badge
     */
    secureLength: {
        type: Number,
        default: 7
    },
    /**
     * Display hint:
     * The hint shows suggestions and warnings on hover
     */
    showHints: {
        type: Boolean,
        default: false
    },
    userInputs: {
        type: Array,
        default: () => []
    }
})
const emits = defineEmits(['score', 'feedback'])

const {t: $t} = useI18n()

const strengthOptions = computed(() => [
    {background: 'bg-red-500 w-1/5', color: 'text-red-500', label: $t('password.extremelyWeak'), icon: 'error'},
    {background: 'bg-orange-500 w-2/5', color: 'text-orange-500', label: $t('password.veryWeak'), icon: 'warning'},
    {background: 'bg-yellow-500 w-3/5', color: 'text-yellow-500', label: $t('password.weak'), icon: 'info'},
    {background: 'bg-lime-500 w-4/5', color: 'text-lime-500', label: $t('password.strong'), icon: 'success'},
    {background: 'bg-green-500 w-full', color: 'text-green-500', label: $t('password.veryStrong'), icon: 'secure'}
])

const password = defineModel({
    type: String,
    default: ''
})

const validUserInputs = computed(() => {
    return (props.userInputs ?? []).filter(input => !!input)
})

const passwordAnalytics = computed(() => {
    return password.value ? zxcvbn(password.value, validUserInputs.value) : null
})

/**
 * passwordStrength is the score calculated by zxcvbn
 * @return {Number} Password Strength Score
 */
const passwordStrength = computed(() => {
    return passwordAnalytics.value?.score ?? null
})

const passwordFeedback = computed(() => {
    return passwordAnalytics.value?.feedback ?? null
})

const passwordCrackTime = computed(() => {
    return passwordAnalytics.value?.crackTimesDisplay ?? null
})

const notLongEnough = computed(() => {
    return password.value.length < props.secureLength
})

watch(() => props.modelValue, (newValue) => {
    emits('feedback', zxcvbn(newValue).feedback)
})

watch(() => passwordStrength, (newScore) => {
    emits('score', newScore)
})
</script>